import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "w-full mtop-10" }
const _hoisted_2 = { class: "row" }
const _hoisted_3 = { class: "mleft-08 font-lBold" }
const _hoisted_4 = { class: "div-appointment w-full mtop-06" }
const _hoisted_5 = { class: "row w-full" }
const _hoisted_6 = { class: "pleft-04 font-size-10" }

import { computed, ref, watch } from 'vue';
import { useI18n } from "vue-i18n";
import { useAppUserStore } from '@/stores/appUser';
import { ApiError, DateFormat, ItemSelectPatient, ItemTag } from '@/models/share.model';
import { format } from 'date-fns';
import { EventInput } from '@fullcalendar/core';
import { AppointmentRequest, CheckAppointmentResponse, PlanningApi } from '@/apiGenerate/medserver';
import { medServerApiConfig } from '@/config/medserver-api.config';
import EsDataCard from '@/components/esDataCard.vue';
import { useCurrentEntryStore } from '@/stores/currentEntry';
import { useUiStore } from '@/stores/ui';
import { useRouter } from 'vue-router';
import PersonService from '@/services/PersonService';
import EsCalendarForm from "@/components/form/esCalendarForm.vue";
import { toastError, toastSuccess, toastWarn } from '@/utils/toast-utils';
import { useToast } from 'primevue';
import SharedService from '@/services/SharedService';
import { useWaitingRoomsStore } from '@/stores/waitingRooms';


export default /*@__PURE__*/_defineComponent({
  __name: 'esWithoutAppointment',
  props: {
    doctors: {},
    refresh: { type: Boolean }
  },
  emits: ["refresh-calendar"],
  setup(__props: any, { emit: __emit }) {

const planningApi = new PlanningApi(medServerApiConfig);

const props = __props;

const { t } = useI18n();

const appUserStore = useAppUserStore();
const currentEntryStore = useCurrentEntryStore();
const waitingRoomsStore = useWaitingRoomsStore();
const uiStore = useUiStore();
const router = useRouter();
const toast = useToast();

const events = ref<Record<string, EventInput>>({});
const isFormModalOpened = ref<boolean>(false);
const currentEvent = ref<EventInput | undefined>(undefined);
const appointmentId = ref<string | undefined>(undefined);
const selectedDoctor = ref<ItemTag>(null);
const isConfirmAppointmentOpened = ref<boolean>(false);
const selectedDoctorUuid = ref<string[]>(null);
const appoitmentTypes = ref<ItemTag>(null);
const headerForm = ref<string>(t('calendar.label.add.patientWithoutAppointmentHeader'));
const displayDelete = ref<boolean>(false);

const emits = __emit;

const currInstituteUuid = computed(() => {
  if (!appUserStore.currentHealthGroupMembership) {return "";}
  return appUserStore.currentHealthGroupMembership.instituteWithRole.instituteWithRoleId || "";
});

const disabledMoveAppointment = computed(() => {
  return currentEntryStore.moveAppointmentFromPatient || currentEntryStore.moveAppointmentFromCalendar;
});

const getEvents = async () => {
  if (props.doctors && props.doctors.length > 0 && currInstituteUuid.value) {
    selectedDoctorUuid.value = props.doctors.filter((doctor) => doctor.selected && doctor.opened).map((doctor) => doctor.id);
    const currentDate = format(new Date(), DateFormat.ISO_SMALL);

    selectedDoctorUuid.value.forEach(async (doctorUuid) => {
      events.value[doctorUuid] = await planningApi.listAppointmentsByPeriod({
        instituteId: currInstituteUuid.value,
        periodStart: currentDate,
        doctor: doctorUuid,
        isPatientWithoutAppointment: true    
      });
    })
  }
};

const setLoadingScreenTexts = (event: EventInput) => {
  const loadingText = PersonService.formatHonorificAndName(null, event.extendedProps.lastname, event.extendedProps.firstname);
  uiStore.setLoadingScreenText(loadingText);

  if (event.extendedProps.birthdate) {
    const loadingSubText = format(new Date(event.extendedProps.birthdate), DateFormat.FRONT_LONG);
    uiStore.setLoadingScreenSubText(loadingSubText);
  }  
}

const redirectPatientFolder = (event: EventInput) => {
    if (event.extendedProps.patientId) {
        setLoadingScreenTexts(event);
        const patientId = event.extendedProps.patientId;

        if (patientId !== currentEntryStore.currentPatient?.personWithRoleId) {
            currentEntryStore.clearCurrentEntry();
        }

        router.push({
            name: "patient",
            params: { patientId: patientId }
        });
    }
}

const openAddAppointment = () => {
  if (disabledMoveAppointment.value) {
    toastWarn(toast, {
        summary: t('common.toast.warning'),
        detail: t('error.MOVE_APPOINTMENT_IN_PROGRESS')
    });
  } else {
    isFormModalOpened.value =  true;
    selectedDoctor.value = undefined;
    headerForm.value = t('calendar.label.add.patientWithoutAppointmentHeader');
    displayDelete.value = false;
    appoitmentTypes.value = undefined;
    currentEvent.value = undefined;
  }
}

const openUpdateAppointment = (event: EventInput) => {
  isFormModalOpened.value =  true;
  currentEvent.value = event;
  selectedDoctor.value = props.doctors.filter((doctor) => doctor.id === event.extendedProps.doctorUuid)[0];
  appointmentId.value = event.extendedProps.appointmentId;
  headerForm.value = t('calendar.label.update.header');
  displayDelete.value = true;
  appoitmentTypes.value = {
    id: event.extendedProps.appointmentTypeId,
    label: '',
    selected: true,
    opened: true
  }
  currentEntryStore.setCurrentEvent(event);
};

const deleteAppointment = async () => {
  await planningApi.deleteAppointment({
    instituteId: currInstituteUuid.value,
    appointmentId: appointmentId.value,
  }).then(() => {
    isFormModalOpened.value = false;
    appointmentId.value = undefined;
    getEvents();
    toastSuccess(toast, {
      summary: t('common.toast.success.cancel'),
      detail:  t('calendar.appointment.delete.message.success')
    });
  }).catch((error: ApiError) => {
    toastError(toast, {
        summary: t('common.toast.error'),
        detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
    });

    // Throw error to set at false the var __alreadyClicked in v-one-click directive
    throw error;
  });
};

const getCurrentPatient = (): ItemSelectPatient => {
    return currentEntryStore.currentPatientCalendar
            ? {id: currentEntryStore.currentPatientCalendar.personWithRoleId, 
              label: PersonService.formatName(currentEntryStore.currentPatientCalendar.lastName, currentEntryStore.currentPatientCalendar.firstName),
              birthdate: currentEntryStore.currentPatientCalendar.birthDate?.split("T")[0].replaceAll("-", " "),
              serialNumber: currentEntryStore.currentPatientCalendar.cnsHealthInsurance?.cnsAffiliationNumber
              }
            : undefined;
}

const closeFormModal = async (appointmentRequest: AppointmentRequest) => {
    if (appointmentRequest) {
      if(appointmentRequest.patientId === undefined && appointmentRequest.personAppointment.firstname === undefined && appointmentRequest.personAppointment.lastname === undefined){
        toastError(toast, {
              summary: t('common.toast.error'),
              detail: t(`error.NO_PATIENT`)
        });
        return;
      }
      const saveAppointmentRequest: AppointmentRequest = SharedService.cleanObject(appointmentRequest);
      
      await planningApi.checkAppointmentExist({instituteId: currInstituteUuid.value, doctorId: saveAppointmentRequest.doctorId, 
                                               appointmentDate: saveAppointmentRequest.date, appointmentTime: saveAppointmentRequest.time,
                                               duration: saveAppointmentRequest.duration, appointmentId: appointmentId.value ?? null})
        .then((checkAppointmentResponse: CheckAppointmentResponse) => {
          if (checkAppointmentResponse.alreadyExist) {
            isConfirmAppointmentOpened.value = true;
          } else {
            if (appointmentId.value) {
              updateAppointment(saveAppointmentRequest);
            } else {
              createAppointment(saveAppointmentRequest);
            }
          }
        }).catch((error: ApiError) => {
          toastError(toast, {
              summary: t('common.toast.error'),
              detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
          });
        });
    }
    
    closeAppointmentFormModal();
}

const updateAppointment = async (saveAppointmentRequest: AppointmentRequest) => {
  planningApi.updateAppointment({
    instituteId: currInstituteUuid.value,
    appointmentId: appointmentId.value,
    appointmentRequest: saveAppointmentRequest
  }).then(async () => {
    toastSuccess(toast, {
        summary: t('common.toast.success.update'),
        detail:  t('calendar.label.message.success.update')
    });
    await getEvents();
    waitingRoomsStore.updateWaitingRooms();
    if (!saveAppointmentRequest.isPatientWithoutAppointment) {
      emits('refresh-calendar')
    }
  }).catch((error: ApiError) => {
    toastError(toast, {
        summary: t('common.toast.error'),
        detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
    });
    // Throw error to set at false the var __alreadyClicked in v-one-click directive
    throw error;
  });
}

const createAppointment = (appointmentRequest: AppointmentRequest) => {
  planningApi.addAppointment({
    instituteId: currInstituteUuid.value,
    appointmentRequest: appointmentRequest}).then(async () => {
      toastSuccess(toast, {
        summary: t('common.toast.success.add'),
        detail:  t('calendar.label.message.success.add')
      });
      await getEvents();
    }).catch((error: ApiError) => {
      toastError(toast, {
        summary: t('common.toast.error'),
        detail: t(`error.${error.status}`)
      });
  });
}


const closeAppointmentFormModal = async () => {
  isFormModalOpened.value = false;
  appointmentId.value = undefined;
  currentEntryStore.setCurrentEvent(undefined);
}

watch(() => props.doctors, async (newList) => {
  if (newList) {
    events.value = {};
    await getEvents();
  }
});

watch(() => props.refresh, async () => {
  if (props.refresh) {
    await getEvents();
  }
});

watch(currInstituteUuid, async (newVal) => {
  if (newVal) {
    await getEvents();
  }
});


return (_ctx: any,_cache: any) => {
  const _component_es_button_icon = _resolveComponent("es-button-icon")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(t)('calendar.withoutAppointment.title')), 1),
        (!disabledMoveAppointment.value)
          ? (_openBlock(), _createBlock(_component_es_button_icon, {
              key: 0,
              icon: "plus",
              folder: "shareIcons",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (openAddAppointment())),
              styles: "icon-grey-style square-14",
              class: "square-29 justify-center mleft-04"
            }))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_4, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(events.value, (eventList) => {
          return (_openBlock(), _createElementBlock(_Fragment, null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(eventList, (event) => {
              return (_openBlock(), _createBlock(EsDataCard, {
                key: event,
                class: "appointment-content mtop-04",
                icon: event.extendedProps?.patientId ? 'stethoscope' : undefined,
                "folder-icon": event.extendedProps?.patientId ? 'shareIcons' : undefined,
                "icon-position-right": true,
                compact: true,
                usePadS: false,
                title: _unref(PersonService).formatHonorificAndName(event.extendedProps?.gender, event.extendedProps?.lastname, event.extendedProps?.firstname),
                backgroundClass: event.extendedProps?.patientId ? 'bg-blue-016' : 'bg-grey-016',
                txtClass: "font-bold-600",
                onIconClick: ($event: any) => (redirectPatientFolder(event)),
                onClick: ($event: any) => (openUpdateAppointment(event))
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("p", _hoisted_6, _toDisplayString(event.title) + " - " + _toDisplayString(event.extendedProps?.doctor), 1)
                  ])
                ]),
                _: 2
              }, 1032, ["icon", "folder-icon", "title", "backgroundClass", "onIconClick", "onClick"]))
            }), 128))
          ], 64))
        }), 256))
      ])
    ]),
    _createVNode(EsCalendarForm, {
      isOpen: isFormModalOpened.value,
      isAllDayEvent: true,
      "selected-date": new Date(),
      header:  headerForm.value,
      doctors: _ctx.doctors,
      selectedDoctor: selectedDoctor.value,
      selectedAppointmentType: appoitmentTypes.value,
      appointment: currentEvent.value,
      patient: getCurrentPatient(),
      displayDelete: displayDelete.value,
      "delete-method": deleteAppointment,
      "confirm-method": closeFormModal,
      onModalClose: _cache[1] || (_cache[1] = ($event: any) => (closeAppointmentFormModal())),
      "display-move-button": false
    }, null, 8, ["isOpen", "selected-date", "header", "doctors", "selectedDoctor", "selectedAppointmentType", "appointment", "patient", "displayDelete"])
  ], 64))
}
}

})